/**
 * Versioned save snapshot — the contract for runs surviving page reload.
 *
 * Format (matches the design-doc Phase-10 spec):
 *
 *   { version: number, tick: number, state: RunStateSnapshot }
 *
 * The `tick` field is the logical-tick index at save time and exists so that
 * v0.2's input-log replay format can stamp events against it (Phase 11 fills
 * it in; v0.1 always writes 0).
 *
 * Migrations are a registry indexed by the source version: `migrations[v]`
 * upgrades v → v+1. v0.1 ships with no migrations because we're at v1; new
 * fields added later become a v1→v2 entry in the registry.
 */
import { runState, type RunStateSnapshot } from './runState';
import { seedStore, type SeedSnapshot } from './seed';
import { computeMaxHp } from '../data/baseHp';
import { expForLevel } from '../data/expCurve';

// v13 (2026-05-16): MVP PR A1 — lab cinematic state machine introduces
// `VAR_MAP_SCENE_PALLET_TOWN_PROFESSOR_OAKS_LAB` + new flags
// (LAB_POST_PICK_DONE, RIVAL_BATTLE_RESOLVED, RIVAL_POST_BATTLE_DONE).
// Existing v12 saves with STARTER_PICKED + OAK_INTRO_DONE set but the
// new state-var defaulting to 0 would resolve tap dialogue against the
// state-0 fallback forever (the player would be stuck in a broken
// sub-state). Destructive drop per CLAUDE.md memory — implicit drop
// via the `if (!migrate)` path in the migration loop below.
//
// v14 (2026-08-02): bucket G2 — bag inventory (`bag: Record<itemId,
// count>`) joins RunStateSnapshot. Destructive drop per CLAUDE.md
// memory, same implicit path; restore()'s `snapshot.bag ?? {}`
// default is a mid-PR safety net, not a migration.
//
// v15 (2026-08-02): stats-integrity — the sim now plays rolled
// nature/IVs/gender, and computeMaxHp derives from the rolled HP IV.
// Existing saves carry hp.max values from the IV-31-era formula that
// would desync from the new sim math (orb rings + HP injection lean on
// stored-max == sim-max). Destructive drop per CLAUDE.md memory,
// implicit no-migration path.
//
// v16 (2026-08-02): roam mode — `mode: 'classic' | 'roam'` +
// `spawnVisits: Record<region, count>` join RunStateSnapshot (New Run
// mode picker + visible roaming wild spawns). A v15 save has neither;
// synthesizing mode='classic' would be safe today but the restore-side
// `?? 'classic'` default is a mid-PR safety net, not a migration —
// destructive drop per CLAUDE.md memory, implicit no-migration path.
const SAVE_VERSION = 16;
const STORAGE_KEY = 'poke-save';

interface SaveSnapshot {
  version: number;
  tick: number;
  state: RunStateSnapshot;
  /** Domain-scoped RNG state. Added in v3 (Phase 11). v2 saves are migrated
   *  by initializing fresh RNG state — the run continues but encounters/
   *  battles produced after the migration aren't continuous with what would
   *  have rolled pre-save. Acceptable: v2 ran on Math.random anyway. */
  rng: SeedSnapshot;
}

/** v→v+1 migrations. Each takes the parsed snapshot (full envelope) and
 *  returns the next-version envelope. Operating on the envelope (not just
 *  `state`) lets v2→v3 add the new top-level `rng` field cleanly.
 *
 *  v1→v2 (2026-05-09 firered swap): drop run-state. v1 saves reference Hoenn
 *  species (treecko/torchic/mudkip/poochyena/...) and Hoenn map IDs which
 *  no longer exist post-swap.
 *
 *  v2→v3 (Phase 11 determinism): add domain-scoped RNG state. The migration
 *  initializes a fresh master seed + per-domain streams; party/routesDrafted
 *  carry over untouched. The discontinuity (rng jumps from non-existent to
 *  fresh state mid-run) is invisible to the player because v2 ran on
 *  Math.random — there's nothing to be continuous with.
 *
 *  v3→v4 (Phase 12 M1, HP persistence): add hp.{current,max} to each party
 *  member + initialize defeatedTrainers=[]. Legacy entries get full HP
 *  (current=max=computeMaxHp(species, level)) — pre-v4 saves had no HP
 *  tracking so "all party at full" is the only sensible default.
 *
 *  v8→v9 (2026-05-12 draft card refresh, destructive): no migration entry.
 *  Adds `gender` + `caughtAt` to DraftOffer / PartyMember / PartyMember-
 *  Snapshot. The new fields are non-nullable on the type contract; the
 *  gender draw also lands at the END of the 'ivs' canonical sequence so
 *  every v8 save's RNG stream would be off by N gender draws after the
 *  bump — synthesizing valid gender + caughtAt for already-played
 *  Pokémon (and re-aligning the rng stream) is fragile fabrication for
 *  zero asked-for value. Same posture as v1→v2 / v6→v7 / v7→v8: the
 *  missing-migration branch (`if (!migrate)`) drops the save and boots
 *  fresh. The careerStore on the separate `poke:career` key is NOT
 *  touched (its schemaVersion stays at 1; gender + caughtAt are OPTIONAL
 *  on CareerEntry for pre-v9 grace).
 *
 *  v9→v10 (status persistence): no migration entry. Adds `status` to
 *  PartyMember. Synthesizing status for already-played Pokémon would be
 *  fabrication; drop posture per the same memory.
 *
 *  v10→v11 (MVP H1 flag/var store, 2026-05-13): no migration entry. Adds
 *  `flags` + `vars` to RunStateSnapshot for scripted-handler state (Oak
 *  intercept, FLAG_HIDE_*, rival lab cinematic, etc.). Synthesizing flag
 *  state for an in-progress v10 run would either (a) leave the player
 *  in a contradictory state (e.g., Brock defeated but FLAG_OAK_INTRO_DONE
 *  unset — game thinks they haven't met Oak yet) or (b) require fabricating
 *  a plausible flag set, which is the same fragility-for-zero-value
 *  tradeoff. Per CLAUDE.md memory: bump destructively.
 *
 *  v11→v12 (MVP PR 12 H9 FLAG_HIDE_* seeding, 2026-05-13): no migration
 *  entry. The set of initial-state FLAG_HIDE_* flags now seeded at
 *  run-start changed cartridge defaults from "filter ignores runState"
 *  to "filter respects runState." A v11 save has flags=[] (empty), so
 *  restoring it post-PR-12 would leave the player with all default-
 *  hidden NPCs (Oak in lab, rival, item balls) visible — contradicting
 *  cartridge state. v11 saves are <24hr old and the friend group has
 *  barely played; destructive drop is the least-magic option per the
 *  CLAUDE.md memory. */
const migrations: Record<number, (snapshot: any) => any> = {
  1: (snapshot) => ({
    version: 2,
    tick: snapshot.tick ?? 0,
    state: { party: [], routesDrafted: [] },
  }),
  2: (snapshot) => {
    seedStore.initFresh();
    return {
      version: 3,
      tick: snapshot.tick ?? 0,
      state: snapshot.state,
      rng: seedStore.serialize(),
    };
  },
  3: (snapshot) => {
    const party = (snapshot.state?.party ?? []).map((m: any) => {
      // Tolerate already-present hp (forward-compat); compute defaults.
      if (m.hp && typeof m.hp.current === 'number' && typeof m.hp.max === 'number') {
        return m;
      }
      // Historical v4 backfill formula (IV=31 era). This chain always
      // hits the destructive-drop wall long before v15, so the value
      // never reaches a live run — kept only so the migration stays
      // byte-faithful to what v4 actually wrote.
      const max = computeMaxHp(m.species, m.level, 31);
      return { ...m, hp: { current: max, max } };
    });
    return {
      version: 4,
      tick: snapshot.tick ?? 0,
      state: {
        ...snapshot.state,
        party,
        defeatedTrainers: snapshot.state?.defeatedTrainers ?? [],
      },
      rng: snapshot.rng,
    };
  },
  4: (snapshot) => {
    // v4→v5 (EXP system): backfill exp = expForLevel(level) so existing
    // party members are at the floor of their current level — no
    // accidental level-down, no "negative exp to next level". Future-
    // saved entries (v5+) carry their actual accumulated exp through.
    const party = (snapshot.state?.party ?? []).map((m: any) => {
      if (typeof m.exp === 'number') return m; // forward-compat
      return { ...m, exp: expForLevel(m.level) };
    });
    return {
      version: 5,
      tick: snapshot.tick ?? 0,
      state: { ...snapshot.state, party },
      rng: snapshot.rng,
    };
  },
  5: (snapshot) => {
    // v5→v6 (resume on reload): add position field. Legacy entries get
    // null — WorldScene treats null as "boot to default bedroom" so the
    // upgrade path is invisible; only saves WRITTEN at v6+ resume to
    // their last tile. Player picking up a v5 save will boot to the
    // bedroom but their party / exp / progress carries through.
    return {
      version: 6,
      tick: snapshot.tick ?? 0,
      state: { ...snapshot.state, position: snapshot.state?.position ?? null },
      rng: snapshot.rng,
    };
  },
  6: (snapshot) => {
    // v6→v7 (Pokédex + Identity, destructive): adds per-instance id to
    // every PartyMember (fixes the HP-by-species collision bug) plus a
    // chain of new fields landing across Steps 1-7 of the Pokédex PR
    // (nature/ivs/ability on PartyMember, pokedex.{drafted,skipped,seenWild},
    // draftedSpeciesThisRun, pendingDraft). Synthesizing valid IVs +
    // natures + abilities for already-played Pokémon (and reconstructing
    // a coherent pokedex / draftedSpeciesThisRun history from a v6 save
    // that doesn't track them) is ~50 lines of fragile fabrication for
    // zero asked-for value — the friend group is ≤10 users and the user
    // dropped v1 saves destructively four days ago (firered swap) without
    // complaint. Same pattern: party + run state cleared; fresh master
    // seed; career data is on a separate poke:career key and is NOT
    // touched. Legacy v6 saves get a v7-empty starting position.
    seedStore.initFresh();
    return {
      version: 7,
      tick: snapshot.tick ?? 0,
      state: {
        party: [],
        routesDrafted: [],
        defeatedTrainers: [],
        position: null,
      },
      rng: seedStore.serialize(),
    };
  },
  7: (snapshot) => {
    // v7→v8 (seamless-world, destructive): per-map state moves from the
    // cartridge long form (`MAP_ROUTE1`, `MAP_PALLET_TOWN`) to the short
    // runtime-canonical name (`Route1`, `PalletTown`). routesDrafted +
    // defeatedTrainers + position.mapName all use the new key. A
    // non-destructive `s/^MAP_//` migration is a ~10-line option (P6 in
    // the design doc) but we pick destructive for the same reasons as
    // v1→v2 and v6→v7: the friend group is ≤10 users, the careerStore on
    // poke:career is untouched, and a few hours of run state is far
    // cheaper than 50 lines of fragile fabrication.
    seedStore.initFresh();
    return {
      version: 8,
      tick: snapshot.tick ?? 0,
      state: {
        party: [],
        routesDrafted: [],
        defeatedTrainers: [],
        position: null,
      },
      rng: seedStore.serialize(),
    };
  },
};

class SaveManager {
  /** Page-hide / battle-end / draft-pick all enqueue saves. The flag prevents
   *  a tween-mid save (e.g. between scene.sleep and the new scene's create)
   *  from racing the snapshot — see design doc § "snapshot atomicity". v0.1
   *  is single-threaded JS so the race surface is small but the pattern is
   *  cheap insurance. */
  private transitioning = false;
  private queuedSave = false;

  save(): void {
    if (this.transitioning) {
      this.queuedSave = true;
      return;
    }
    this.writeSnapshot();
  }

  /** Best-effort flush ignoring the transitioning flag; called from
   *  pagehide / visibilitychange handlers where we'd rather have a
   *  slightly-stale snapshot than nothing. */
  flush(): void {
    this.writeSnapshot();
  }

  private writeSnapshot(): void {
    const snapshot: SaveSnapshot = {
      version: SAVE_VERSION,
      tick: 0,
      state: runState.serialize(),
      rng: seedStore.serialize(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (err) {
      // QuotaExceededError, private-browsing block, etc. Don't break the
      // game over a save failure — just log.
      console.warn('[save] write failed:', err);
    }
  }

  /**
   * Load + apply to runState. Returns true if a save was found and applied,
   * false if no save exists, throws if the save is corrupt past recovery.
   */
  load(): boolean {
    let raw: string | null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      console.warn('[save] read failed:', err);
      return false;
    }
    if (!raw) return false;

    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      console.warn('[save] corrupt JSON, ignoring:', err);
      return false;
    }

    let snapshot: any = parsed;
    let version: number = parsed.version;
    while (version < SAVE_VERSION) {
      const migrate = migrations[version];
      if (!migrate) {
        console.warn(`[save] no migration from v${version} → v${version + 1}, dropping save`);
        return false;
      }
      snapshot = migrate(snapshot);
      version = snapshot.version;
    }
    if (version > SAVE_VERSION) {
      // Future-version save (rolled back to an older client). Discard it
      // rather than corrupt the run.
      console.warn(`[save] save is from v${version}, current is v${SAVE_VERSION} — dropping`);
      return false;
    }

    const final = snapshot as SaveSnapshot;
    runState.restore(final.state);
    seedStore.restore(final.rng);
    return true;
  }

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore — clear is best-effort.
    }
  }

  beginTransition(): void {
    this.transitioning = true;
  }

  endTransition(): void {
    this.transitioning = false;
    if (this.queuedSave) {
      this.queuedSave = false;
      this.writeSnapshot();
    }
  }
}

export const saveManager = new SaveManager();

/**
 * Wire pagehide + visibilitychange so a save flushes when the tab is closed
 * or backgrounded. Both fire reliably on mobile Safari. The visibility check
 * catches "user hits home button" without unloading; pagehide catches close.
 */
export function installAutoSaveHandlers(): void {
  window.addEventListener('pagehide', () => saveManager.flush());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveManager.flush();
  });
}
